import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * In-chat "Find in conversation" navigation, driven by the DOM (#352).
 *
 * THE INVERSION. The old implementation counted matches in `message.content` (the
 * raw markdown source) and asked the renderer to mint an `id="search-match-N"` for
 * the Nth hit. Those two numberings cannot agree: the rendered text is not the
 * source text (markdown syntax is consumed, `[FUNCT]` blocks are stripped, a user
 * bubble renders `message.display`), the offset was resolved with
 * `indexOf(segment)` — the FIRST occurrence — so repeated text produced duplicate
 * ids, and several visible surfaces (links, quotes, table headers) emitted no marks
 * at all. The counter therefore said "1 / 6" while the DOM held a different set,
 * and `document.getElementById('search-match-3')` returned null. The jump was
 * guarded by `if (el)`, so it silently did nothing — "the page stays static".
 *
 * Here the DOM is the single source of truth. After each render we walk the chat
 * container for `[data-search-match]` in document order; that list IS the match
 * set. Its length is the counter's denominator — so the count is, by construction,
 * exactly the number of highlights the user can see and reach — and navigation
 * scrolls to `matches[i]`, an element we are holding, so it can never miss.
 *
 * Numbering is stamped back onto the elements as `id="search-match-N"` so the ids
 * stay meaningful (and existing selectors keep working), but they are now a
 * CONSEQUENCE of document order rather than an input to it.
 *
 * The active match is styled imperatively (`data-search-active`) rather than by
 * passing an index down the tree. That is what makes the arrows instant: React
 * re-rendered — and re-parsed the markdown of — every message in the thread for
 * what is one CSS class on one element.
 */

const MATCH_SELECTOR = '[data-search-match]';
const ACTIVE_ATTR = 'data-search-active';

export default function useInChatSearch(containerRef, searchQuery, messages) {
    const [totalMatches, setTotalMatches] = useState(0);
    const [activeIndex, setActiveIndex] = useState(-1);
    const matchesRef = useRef([]);
    // Survives the re-collection that follows a new message arriving mid-search, so
    // a streaming reply does not yank the user back to match #1.
    const activeRef = useRef(-1);

    const paint = useCallback((index) => {
        const matches = matchesRef.current;
        for (const el of matches) el.removeAttribute(ACTIVE_ATTR);
        const el = matches[index];
        if (el) el.setAttribute(ACTIVE_ATTR, 'true');
        return el || null;
    }, []);

    const scrollTo = useCallback((index, behavior = 'smooth') => {
        const el = paint(index);
        if (el) el.scrollIntoView({ behavior, block: 'center' });
    }, [paint]);

    // Collect after every render that can change the mark set. useLayoutEffect (not
    // useEffect) so the counter is correct in the same commit the highlights appear
    // in — with useEffect the user sees "No results" flash for a frame.
    useLayoutEffect(() => {
        const root = containerRef.current;
        if (!root || !searchQuery) {
            matchesRef.current = [];
            activeRef.current = -1;
            setTotalMatches(0);
            setActiveIndex(-1);
            return;
        }

        const matches = Array.from(root.querySelectorAll(MATCH_SELECTOR));
        matchesRef.current = matches;
        // Document order IS the match order, so the id is now always resolvable.
        matches.forEach((el, i) => { el.id = `search-match-${i}`; });
        setTotalMatches(matches.length);

        if (matches.length === 0) {
            activeRef.current = -1;
            setActiveIndex(-1);
            return;
        }

        // Keep the user where they were if that position still exists; otherwise
        // start at the first match.
        const next = activeRef.current >= 0 && activeRef.current < matches.length
            ? activeRef.current
            : 0;
        activeRef.current = next;
        setActiveIndex(next);
        paint(next);
    }, [containerRef, searchQuery, messages, paint]);

    // Scroll to the first match when a NEW term resolves. Separated from the
    // collection effect so that re-collecting (a streaming token, an edited
    // message) never re-scrolls the user.
    const lastScrolledTerm = useRef(null);
    useEffect(() => {
        if (!searchQuery) { lastScrolledTerm.current = null; return; }
        if (lastScrolledTerm.current === searchQuery) return;
        if (totalMatches === 0) return;
        lastScrolledTerm.current = searchQuery;
        scrollTo(0, 'auto');
    }, [searchQuery, totalMatches, scrollTo]);

    const goTo = useCallback((index) => {
        const n = matchesRef.current.length;
        if (n === 0) return;
        const wrapped = ((index % n) + n) % n;   // wrap in both directions
        activeRef.current = wrapped;
        setActiveIndex(wrapped);
        scrollTo(wrapped);
    }, [scrollTo]);

    const next = useCallback(() => goTo(activeRef.current + 1), [goTo]);
    const previous = useCallback(() => goTo(activeRef.current - 1), [goTo]);

    const reset = useCallback(() => {
        for (const el of matchesRef.current) el.removeAttribute(ACTIVE_ATTR);
        matchesRef.current = [];
        activeRef.current = -1;
        lastScrolledTerm.current = null;
        setTotalMatches(0);
        setActiveIndex(-1);
    }, []);

    return { totalMatches, activeIndex, next, previous, goTo, reset };
}
